// Onboardings por rol (2026-09-10). Fuente única de qué carril, qué
// superficie y qué escenas le corresponden a cada persona, y de las claves
// de storage que deciden si el tour y el coach del primer uso ya se vieron.
// Dominio puro: nada de React, y el único storage que toca es el que le
// inyectan (default `localStorage` resuelto adentro de cada función, nunca
// al cargar el módulo).
//
// Decisión del fundador (2026-09-10): cuatro carriles, el rol decide, sin
// variante por institución.

import { esCapaEjercicio, esElegibleTurno, veJefatura } from './perfil';
import { esTens } from './tens';

// ── Catálogo cerrado (todo lo que viaja a telemetría sale de acá) ──────────

export const CARRILES = Object.freeze(['enfermera', 'tens', 'estudiante', 'jefatura']);

export const SUPERFICIES = Object.freeze(['turno', 'tens', 'pae', 'jefatura']);

// ── El video del tour (2026-09-12) ───────────────────────────────────────
// Decisión del fundador: UN video general de MiPAE (50 s, vertical, "mientras
// aprendes, cuidas y supervisas") es el onboarding de todos; las pantallas
// CSS que cambiaban con texto encima se borraron. El carril solo decide el
// CTA de cierre y la telemetría. Los videos por carril siguen en
// docs/pitch/onboarding-<carril>/ como material, sin cablear.
export const VIDEO_TOUR = Object.freeze({ src: '/onboarding/completo.mp4', poster: '/onboarding/completo.jpg' });
export function videoDe() {
  return VIDEO_TOUR;
}

export const SALIDAS_TOUR = Object.freeze(['saltar', 'explorar', 'probar']);

export const ORIGENES_TOUR = Object.freeze(['auto', 'de_nuevo', 'preview']);

// Beats del primer uso guiado, por carril. Los textos y el cableado viven en
// cada superficie (RegistroTurno, HomeTens, PAERapido, TableroJefatura); acá
// solo el catálogo de ids para telemetría y guardas de "ya visto".
export const BEATS = Object.freeze({
  enfermera: Object.freeze(['hub_vacio', 'dictado', 'evolucion']),
  tens: Object.freeze(['tens_hub', 'tens_observacion']),
  estudiante: Object.freeze(['pae_primero']),
  jefatura: Object.freeze(['jefatura_tablero']),
});

// ── Carril y superficie ──────────────────────────────────────────────────

/**
 * ¿Qué carril de onboarding le corresponde a este perfil? Precedencia fijada
 * por test: jefatura > tens > enfermera (capa ejercicio) > estudiante.
 *
 * Sin rol o perfil nulo cae a estudiante porque el PAE demo es la única
 * superficie sin riesgo que tiene sentido mostrarle a alguien sin rol
 * confirmado todavía (decisión de implementación). Docencia y postgrado son
 * enfermero desde el 2026-10-04 (lib/roles.js), así que llegan a enfermera
 * por `esCapaEjercicio`.
 * @param {object|null} profile
 * @returns {'enfermera'|'tens'|'estudiante'|'jefatura'}
 */
export function carrilDe(profile) {
  if (veJefatura(profile)) return 'jefatura';
  if (esTens(profile)) return 'tens';
  if (esCapaEjercicio(profile)) return 'enfermera';
  return 'estudiante'; // estudiante, sin rol, perfil nulo
}

/**
 * ¿Qué superficie primaria le corresponde a este perfil? Estudiante depende
 * de si su etapa lo hace elegible para MiPAE en turno (internado en curso) o
 * no (PAE). El carril se recalcula con `carrilDe` para no pedirle al llamador
 * que mantenga los dos sincronizados.
 * @param {object|null} profile
 * @returns {'turno'|'tens'|'pae'|'jefatura'}
 */
export function superficieDe(profile) {
  const carril = carrilDe(profile);
  if (carril === 'jefatura') return 'jefatura';
  if (carril === 'tens') return 'tens';
  if (carril === 'enfermera') return 'turno';
  return esElegibleTurno(profile) ? 'turno' : 'pae';
}

// Las 7 escenas de hoy: preview sin login (carril null) y fallback de un
// carril desconocido.
/**
 * Texto del CTA de cierre según la superficie de destino.
 *
 * Corregido el 2026-09-14 por el fundador: el botón dice a DONDE lleva y con
 * qué empiezas, en el vocabulario de la app. "Abrir el tablero" y "Explorar
 * por mi cuenta" se cayeron por vagos: explorar no es una acción, y el módulo
 * se llama "MiPAE en turno".
 *
 * @param {'enfermera'|'tens'|'estudiante'|'jefatura'|null} carril
 * @param {'turno'|'tens'|'pae'|'jefatura'|null} superficie
 * @returns {string}
 */
export function ctaCierre(carril, superficie) {
  if (carril == null) return 'Entrar a MiPAE'; // preview sin login
  if (superficie === 'turno') return 'Empezar con una cama de práctica';
  if (superficie === 'tens') return 'Ir a MiPAE en turno';
  if (superficie === 'pae') return 'Empezar mi primer PAE';
  if (superficie === 'jefatura') return 'Ver el panel de gestión';
  return 'Entrar a MiPAE';
}

// ── Claves del tour (gate "ya lo vio") ──────────────────────────────────

// Re-estrena el tour a TODA la base una vez (decisión del fundador
// 2026-09-10: la versión _v3 trae los carriles, vale la pena que todos la
// vean, aunque ya hayan visto una versión vieja). Por eso ambas claves se
// versionan juntas, mismo patrón que _v1 → _v2 en App.jsx.
export const CLAVE_TOUR_DISPOSITIVO = 'mipae_onboarding_video_v3';

export function claveTourUsuario(uid) {
  return `mipae_intro_visto_v3_${uid}`;
}

function storagePorDefecto() {
  return typeof localStorage !== 'undefined' ? localStorage : null;
}

/**
 * ¿Falta mostrar el tour? Fail-safe igual al de hoy en App.jsx: sin storage
 * (o si lanza) se considera pendiente, así que se muestra una vez.
 * @param {string} uid
 * @param {Storage|null} [storage]
 * @returns {boolean}
 */
export function tourPendiente(uid, storage = storagePorDefecto()) {
  if (!storage) return true;
  try {
    return !(storage.getItem(CLAVE_TOUR_DISPOSITIVO) && storage.getItem(claveTourUsuario(uid)));
  } catch {
    return true;
  }
}

/**
 * Marca el tour como visto en ambas claves. Tolera que el storage falle.
 * @param {string} uid
 * @param {Storage|null} [storage]
 */
export function marcarTourVisto(uid, storage = storagePorDefecto()) {
  if (!storage) return;
  try {
    storage.setItem(CLAVE_TOUR_DISPOSITIVO, '1');
    storage.setItem(claveTourUsuario(uid), '1');
  } catch {
    // sin storage disponible: igual se comporta como visto para esta sesión
  }
}

// ── Coach one-shot del primer uso ("mipae:turno:coach") ─────────────────
// Movido desde components/preparaTurno/RegistroTurno.jsx (mismo storage,
// misma clave, misma semántica exacta: sin storage se considera visto, para
// no insistir). `ayudaCapturaDisponible`/`consumirAyudaCaptura` (cuota
// aparte, no one-shot) se quedan en RegistroTurno.jsx pero comparten el
// mismo objeto JSON de esta clave a través de `leerCoach`/`guardarCoach`.
export const COACH_KEY = 'mipae:turno:coach';

/**
 * Lee el objeto JSON completo de la clave del coach. Nunca lanza: sin
 * storage o con JSON corrupto devuelve `{}`.
 * @returns {object}
 */
export function leerCoach() {
  try {
    return JSON.parse(localStorage.getItem(COACH_KEY) || '{}');
  } catch {
    return {};
  }
}

/**
 * Escribe el objeto JSON completo de vuelta a la clave del coach. Tolera que
 * el storage falle (privado, cuota agotada, etc).
 * @param {object} v
 */
export function guardarCoach(v) {
  try {
    localStorage.setItem(COACH_KEY, JSON.stringify(v));
  } catch {
    // noop: sin storage no hay dónde persistir, no es fatal
  }
}

/**
 * ¿Ya se vio este beat one-shot? Sin storage (o si lanza) se considera
 * VISTO, para no insistir.
 * @param {string} beat
 * @returns {boolean}
 */
export function coachVisto(beat) {
  // Con su propio try/catch (no via leerCoach) porque la semantica original
  // distingue "clave ausente" (false: mostrar) de "sin storage" (true: no
  // insistir en cada apertura a quien no puede recordar que ya lo vio).
  try { return !!JSON.parse(localStorage.getItem(COACH_KEY) || '{}')[beat]; }
  catch { return true; }
}

/**
 * Marca un beat one-shot como visto, conservando el resto del objeto (p. ej.
 * la cuota `ayuda_captura` que vive en la misma clave).
 * @param {string} beat
 */
export function marcarCoach(beat) {
  const v = leerCoach();
  v[beat] = true;
  guardarCoach(v);
}
